import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import Text from '../components/shared/Text';

function RestocksIndex() {
    const [restocks, setRestocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total_pages: 1,
        total_count: 0,
        current_page: 1,
        per_page: 25
    });

    useEffect(() => {
        loadRestocks();
    }, [search, currentPage]);

    const loadRestocks = async () => {
        try {
            setLoading(true);
            const response = await api.getRestocks({ q: search, page: currentPage });
            if (response.success) {
                setRestocks(Array.isArray(response.data) ? response.data : (response.data.restocks || []));
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            } else {
                setError(response.message || 'Failed to load restocks');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const statusColors: any = {
        'draft': 'bg-slate-50 text-slate-500 border-slate-100',
        'ordered': 'bg-blue-50 text-blue-600 border-blue-100',
        'received': 'bg-emerald-50 text-emerald-600 border-emerald-100',
        'paid': 'bg-emerald-50 text-emerald-600 border-emerald-100',
        'cancelled': 'bg-rose-50 text-rose-600 border-rose-100'
    };

    if (loading && restocks.length === 0) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Loading restocks...</Text>
            </div>
        );
    }

    return (
        <div className="mt-16 pb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <Text variant="h1" color="primary">Restocks</Text>
                    <Text variant="body" color="slate" className="mt-1">
                        Manage and track product restocks from vendors ({pagination.total_count} found)
                    </Text>
                </div>
                <Link to="/restocks/new" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/10 text-xs tracking-wider">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    NEW RESTOCK
                </Link>
            </div>

            <div className="mb-6">
                <div className="relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search restocks by number or vendor..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                    />
                </div>
            </div>

            {error && (
                <div className="mb-6 bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                    <Text variant="small" weight="medium" className="text-rose-700">{error}</Text>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Restock #</Text></th>
                                <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Vendor</Text></th>
                                <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Date</Text></th>
                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Amount</Text></th>
                                <th className="px-6 py-4 text-center"><Text variant="label" color="slate">Status</Text></th>
                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Actions</Text></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {restocks.map((restock: any) => (
                                <tr key={restock.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <Text variant="mono" weight="bold" color="primary">{restock.purchase_number}</Text>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Text variant="body" weight="medium" color="primary">
                                            {restock.vendor?.name || restock.vendor_name || 'N/A'}
                                        </Text>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <Text variant="small" color="primary">
                                                {new Date(restock.created_at || restock.purchase_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </Text>
                                            <Text variant="small" color="slate" className="capitalize">
                                                {new Date(restock.created_at || restock.purchase_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                            </Text>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Text variant="mono" weight="bold" color="primary">
                                            ₹{restock.total_amount?.toLocaleString()}
                                        </Text>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${statusColors[restock.status] || 'bg-slate-50 text-slate-500 border-slate-100'}`}>
                                            {restock.status?.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Link to={`/purchases/${restock.id}`} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View Details">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            </Link>
                                            <Link to={`/restocks/${restock.id}/edit`} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit Restock">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {restocks.length === 0 && !loading && (
                    <div className="py-20 text-center">
                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-3.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <Text variant="h3" color="primary">No restocks found</Text>
                        <Text variant="small" color="slate" className="mt-1">Try adjusting your search filters or create a new restock</Text>
                    </div>
                )}
            </div>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default RestocksIndex;
